import { LabeledInfo } from "./LabeledInfo";
import {
  AssignmentIndOutlined,
  CheckCircleOutline,
  ExpandMore,
  FactCheckOutlined,
  SearchOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  claimQualityInspection,
  claimDerogation,
  decideQualityInspection,
  decideDerogation,
  loadDerogation,
  loadDerogations,
  loadQualityInspection,
  loadQualityInspections,
  loadProcessPoints,
  DerogationDisposition,
  QualityDisposition,
  QualityInspection
} from "./api";
import { PageHeader } from "./PageHeader";
import { MobileActionBar } from "./MobileActionBar";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";
import { SourceTrace } from "./SourceTrace";
import { ListRowActions } from "./ListRowActions";
import { compareOptionalDates, matchesListSyntax } from "./listSearch";
import { operationalStatusColor, operationalStatusLabel } from "./operationalStatus";
import { FefoDerogationQueue } from "./FefoDerogationQueue";
import { loadFefoExceptions } from "./api/fefo";

interface QualityInspectionPageProps {
  onBack: () => void;
  permissions: string[];
  mode?: "quality" | "derogations";
}

export function QualityInspectionPage({
  onBack,
  initialTaskId = null,
  initialOpenDetail = false,
  permissions,
  mode = "quality"
}: QualityInspectionPageProps & {
  initialTaskId?: string | null;
  initialOpenDetail?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const initialTaskOpened = useRef(false);
  const [comment, setComment] = useState("");
  const [extensionDays, setExtensionDays] = useState<number | "">("");
  const [departurePointId, setDeparturePointId] = useState("");
  const [disposition, setDisposition] =
    useState<QualityDisposition | DerogationDisposition>("RELEASED");
  const [error, setError] = useState<string | null>(null);
  const [listTab, setListTab] = useState<"open" | "closed">("open");
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [syntaxSearch, setSyntaxSearch] = useState("");
  const [mobileSelectedId, setMobileSelectedId] = useState<string | null>(null);
  const isDerogation = mode === "derogations";
  const canExecute = permissions.includes(isDerogation
    ? "derogation:execute" : "quality_inspection:execute");
  const canRelease = isDerogation ? canExecute
    : permissions.includes("quality_disposition:release");
  const canRepack = isDerogation ? canExecute
    : permissions.includes("quality_disposition:repack");
  const canReturn = isDerogation ? canExecute
    : permissions.includes("quality_disposition:return");
  const canScrap = isDerogation ? canExecute
    : permissions.includes("quality_disposition:scrap");
  const reasonLabel = (reason: string) => t(`quality.reasons.${reason}`, {
    defaultValue: reason.replaceAll("_", " ")
  });
  const dispositionLabel = (value: string) => t(
    `quality.dispositions.${value}`, { defaultValue: value.replaceAll("_", " ") }
  );

  const inspections = useQuery({
    queryKey: [isDerogation ? "derogations" : "quality-inspections"],
    queryFn: isDerogation ? loadDerogations : loadQualityInspections
  });
  const fefoExceptions = useQuery({
    queryKey: ["fefo-exceptions"],
    queryFn: loadFefoExceptions,
    enabled: isDerogation
  });
  useEffect(() => {
    if (!initialTaskId || initialTaskOpened.current || !inspections.data) return;
    const inspection = inspections.data.find((entry) => entry.id === initialTaskId);
    if (inspection) {
      initialTaskOpened.current = true;
      setListTab(["COMPLETED", "CANCELLED"].includes(inspection.status)
        ? "closed" : "open");
      if (initialOpenDetail) {
        setExpandedIds((current) => current.includes(initialTaskId)
          ? current : [...current, initialTaskId]);
      }
      window.setTimeout(() => document.getElementById(
        `quality-${initialTaskId}`
      )?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
    }
  }, [initialOpenDetail, initialTaskId, inspections.data]);
  const detail = useQuery({
    queryKey: ["quality-inspection", selectedId],
    queryFn: () => isDerogation ? loadDerogation(selectedId!)
      : loadQualityInspection(selectedId!),
    enabled: Boolean(selectedId)
  });
  const departurePoints = useQuery({
    queryKey: ["process-points", detail.data?.warehouseId],
    queryFn: () => loadProcessPoints(detail.data!.warehouseId),
    enabled: Boolean(detail.data?.warehouseId)
  });
  const eligibleDeparturePoints = useMemo(
    () => (departurePoints.data ?? []).filter((point) =>
      point.status === "ACTIVE"
      && (disposition === "REPACKAGING"
        ? point.type === "REPACKING" || point.defaultForRepacking
        : point.putawaySourceEnabled)
    ),
    [departurePoints.data, disposition]
  );
  useEffect(() => {
    setComment(detail.data?.dispositionComment ?? "");
    setDisposition(detail.data?.workflowState === "PENDING_EXECUTION"
      ? detail.data.partnerDecision === "ACCEPTED"
        ? "ACCEPT_NEW_EXPIRY" : "QUARANTINE"
      : detail.data?.disposition
      ?? (isDerogation ? "ACCEPT_NEW_EXPIRY"
        : canRelease ? "RELEASED"
        : canRepack ? "REPACKAGING"
          : canReturn ? "RETURN_TO_SUPPLIER" : "SCRAPPED"));
    setExtensionDays(detail.data?.decidedExtensionDays
      ?? detail.data?.extensionDays ?? "");
    setError(null);
  }, [
    detail.data?.id,
    detail.data?.dispositionComment,
    detail.data?.workflowState,
    detail.data?.partnerDecision,
    detail.data?.decidedExtensionDays,
    detail.data?.extensionDays,
    canRelease,
    canRepack,
    canReturn,
    isDerogation
  ]);
  useEffect(() => {
    const points = eligibleDeparturePoints;
    setDeparturePointId(
      points.find((point) => disposition === "REPACKAGING"
        ? point.defaultForRepacking : point.defaultForQuality)?.id
      ?? points.find((point) =>
        point.code === detail.data?.processPointCode
        && point.putawaySourceEnabled)?.id
      ?? points[0]?.id
      ?? ""
    );
  }, [
    disposition,
    detail.data?.processPointCode,
    eligibleDeparturePoints
  ]);

  const refresh = async (updated: QualityInspection) => {
    queryClient.setQueryData(["quality-inspection", updated.id], updated);
    await queryClient.invalidateQueries({ queryKey: [isDerogation
      ? "derogations" : "quality-inspections"] });
    await queryClient.invalidateQueries({ queryKey: ["putaway-requests"] });
    await queryClient.invalidateQueries({ queryKey: ["putaway-alerts"] });
  };
  const claim = useMutation({
    mutationFn: isDerogation ? claimDerogation : claimQualityInspection,
    onSuccess: refresh,
    onError: (cause) => setError(cause instanceof Error ? cause.message :
      t("quality.errors.claim"))
  });
  const decide = useMutation({
    mutationFn: ({ inspection, value }: {
      inspection: QualityInspection; value: string;
    }) => isDerogation
      ? decideDerogation(inspection, disposition as DerogationDisposition, value,
        disposition === "ACCEPT_NEW_EXPIRY" && extensionDays !== ""
          ? extensionDays : null)
      : decideQualityInspection(
        inspection, disposition as QualityDisposition,
        disposition === "RELEASED" || disposition === "REPACKAGING"
          ? departurePointId : null,
        value
      ),
    onSuccess: refresh,
    onError: (cause) => setError(cause instanceof Error ? cause.message :
      t("quality.errors.decision"))
  });
  const filteredInspections = useMemo(() =>
    [...(inspections.data ?? [])].filter((inspection) =>
      ["COMPLETED", "CANCELLED"].includes(inspection.status)
        === (listTab === "closed")
      && (!warehouseFilter || inspection.warehouseCode === warehouseFilter)
      && (!syntaxSearch.trim() || matchesListSyntax({
        id: `${inspection.id} ${inspection.inspectionNumber} ${inspection.huNumber}`,
        article: inspection.itemCodes,
        serie: inspection.serialNumbers,
        entrepot: inspection.warehouseCode,
        gare: inspection.processPointCode,
        raison: inspection.reasonCode,
        statut: inspection.status,
        decision: inspection.disposition,
        date: `${inspection.createdAt} ${inspection.completedAt ?? ""}`
      }, syntaxSearch))
    ).sort((left, right) => compareOptionalDates(
      listTab === "closed" ? left.completedAt : left.createdAt,
      listTab === "closed" ? right.completedAt : right.createdAt,
      sortDirection
    )), [
      inspections.data, listTab, sortDirection, syntaxSearch, warehouseFilter
    ]);
  const queueInspections = inspections.data ?? [];
  const fefoOpenCount = isDerogation ? (fefoExceptions.data ?? []).filter(
    (exception) => ["OPEN", "UNDER_REVIEW"].includes(exception.status)
  ).length : 0;
  const fefoClosedCount = isDerogation ? (fefoExceptions.data ?? []).filter(
    (exception) => ["APPROVED", "REJECTED", "RESOLVED", "CLOSED"].includes(exception.status)
  ).length : 0;
  const openCount = queueInspections.filter(
    (inspection) => !["COMPLETED", "CANCELLED"].includes(inspection.status)
  ).length + fefoOpenCount;
  const closedCount = queueInspections.filter(
    (inspection) => ["COMPLETED", "CANCELLED"].includes(inspection.status)
  ).length + fefoClosedCount;
  const warehouseCodes = [...new Set(
    inspections.data?.map((inspection) => inspection.warehouseCode) ?? []
  )].sort();
  useEffect(() => setMobileSelectedId(null), [listTab]);
  const selectedMobileInspection = filteredInspections.find((inspection) =>
    inspection.id === mobileSelectedId) ?? null;
  const canDecide = canRelease || canRepack || canReturn || canScrap;
  const mobilePrimaryLabel = selectedMobileInspection?.status === "OPEN"
    ? t("quality.actions.claim")
    : selectedMobileInspection?.status === "IN_PROGRESS" && canDecide
      ? t(selectedMobileInspection.workflowState === "PENDING_EXECUTION"
        ? "quality.actions.review" : "quality.actions.decide") : selectedMobileInspection
        ? t("quality.actions.view") : t("quality.actions.select");
  const mobilePrimaryAvailable = Boolean(selectedMobileInspection)
    && (selectedMobileInspection?.status !== "OPEN" || canExecute)
    && (selectedMobileInspection?.status !== "IN_PROGRESS" || canDecide);
  const executeMobilePrimary = () => {
    if (!selectedMobileInspection) return;
    if (selectedMobileInspection.status === "OPEN" && canExecute) {
      claim.mutate(selectedMobileInspection);
    } else {
      setSelectedId(selectedMobileInspection.id);
    }
  };
  const statusTabs = [
    { value: "open" as const, label: t("quality.tabs.open"),
      mobileLabel: t("quality.tabsMobile.open"), count: openCount },
    { value: "closed" as const, label: t("quality.tabs.closed"),
      mobileLabel: t("quality.tabsMobile.closed"), count: closedCount }
  ];

  return (
    <Stack spacing={1.5} sx={{
      minHeight: "100%", height: "auto", overflow: "visible"
    }}>
      <MobileOperationalHeader moduleLabel={t(isDerogation ? "quality.derogations.module" : "quality.module")}
        search={syntaxSearch} onSearchChange={setSyntaxSearch}
        searchPlaceholder={t("quality.searchPlaceholder")} onBack={onBack} />
      <Box sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}>
        <PageHeader title={t(isDerogation ? "quality.derogations.title" : "quality.title")} compact onBack={onBack} />
      </Box>

      <ResponsiveStatusTabs value={listTab} items={statusTabs}
        onChange={setListTab} />

      <Card variant="outlined" sx={{ display: { xs: "none", sm: "block" },
        flexShrink: 0 }}>
        <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField size="small" label={t("common.filters.search")}
              placeholder={t("quality.filters.syntaxPlaceholder")}
              value={syntaxSearch}
              onChange={(event) => setSyntaxSearch(event.target.value)}
              helperText={t("quality.filters.syntaxHelp")}
              InputProps={{ startAdornment:
                <SearchOutlined color="action" sx={{ mr: 1 }} /> }}
              sx={{ flex: 2 }} />
            <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
              <InputLabel>{t("quality.filters.warehouse")}</InputLabel>
              <Select label={t("quality.filters.warehouse")} value={warehouseFilter}
                onChange={(event) => setWarehouseFilter(event.target.value)}>
                <MenuItem value="">{t("common.filters.all")}</MenuItem>
                {warehouseCodes.map((code) => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t("quality.filters.dateOrder")}</InputLabel>
              <Select label={t("quality.filters.dateOrder")} value={sortDirection}
                onChange={(event) => setSortDirection(event.target.value as
                  "asc" | "desc")}>
                <MenuItem value="asc">{t("common.filters.ascending")}</MenuItem>
                <MenuItem value="desc">{t("common.filters.descending")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>
      {inspections.isLoading && <CircularProgress />}
      {inspections.isError && (
        <Alert severity="error">{t("quality.errors.load")}</Alert>
      )}
      {inspections.data?.length === 0 && (
        <Alert severity="info">{t("quality.empty.all")}</Alert>
      )}
      {error && !selectedId && (
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      )}
      <Stack spacing={2} sx={{
        flex: "none", minHeight: 0, overflow: "visible",
        pr: .5, pb: { xs: 11, md: 2 }
      }}>
        {isDerogation && <FefoDerogationQueue canExecute={canExecute}
          tab={listTab} />}
        {filteredInspections.map((inspection) => (
          <Card key={inspection.id} id={`quality-${inspection.id}`}
            variant="outlined" onClick={(event) => {
              if ((event.target as HTMLElement).closest(
                "button,a,[role='button']")) return;
              setMobileSelectedId(inspection.id);
            }} sx={{
              cursor: { xs: "pointer", sm: "default" },
              outline: { xs: mobileSelectedId === inspection.id
                ? "2px solid" : "none", sm: "none" },
              outlineColor: "primary.main",
              borderColor: inspection.id === initialTaskId
                ? "primary.main" : undefined,
              borderWidth: inspection.id === initialTaskId ? 2 : undefined
            }}>
              <CardContent>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
                  alignItems={{ sm: "center" }}>
                  <FactCheckOutlined color="secondary" />
                  {isDerogation ? (
                  <Stack spacing={1.25} sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                      <LabeledInfo label={t("quality.derogationCard.reference")} prominent>{inspection.inspectionNumber}</LabeledInfo>
                      <LabeledInfo label={t("quality.derogationCard.client")}>{inspection.clientName ?? inspection.clientCode ?? "—"}</LabeledInfo>
                      <LabeledInfo label={t("quality.derogationCard.warehouse")}>{inspection.warehouseCode}</LabeledInfo>
                    </Stack>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}
                      sx={{ p: 1.25, bgcolor: "background.default", borderRadius: 1 }}>
                      <LabeledInfo label={t("quality.derogationCard.item")} prominent>{inspection.itemCodes}</LabeledInfo>
                      <LabeledInfo label={t("quality.derogationCard.hu")} prominent>{inspection.huNumber}</LabeledInfo>
                      <LabeledInfo label={t("quality.derogationCard.quantity")} prominent>{inspection.quantity} {inspection.uom}</LabeledInfo>
                      <LabeledInfo label={t("quality.derogationCard.location")}>{inspection.processPointCode}</LabeledInfo>
                      {inspection.serialNumbers && <LabeledInfo label={t("quality.derogationCard.serial")}>{inspection.serialNumbers}</LabeledInfo>}
                    </Stack>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                      <LabeledInfo label={t("quality.derogationCard.reason")}>{reasonLabel(inspection.reasonCode)}</LabeledInfo>
                      {inspection.asnNumber && <LabeledInfo label={t("quality.derogationCard.asn")}>{inspection.asnNumber}</LabeledInfo>}
                      {inspection.receiptNumber && <LabeledInfo label={t("quality.derogationCard.receipt")}>{inspection.receiptNumber}</LabeledInfo>}
                    </Stack>
                  </Stack>
                  ) : (
                  <Stack spacing={1.25} sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
                      <LabeledInfo label={t("quality.inspectionCard.reference")} prominent>{inspection.inspectionNumber}</LabeledInfo>
                      <LabeledInfo label={t("quality.derogationCard.hu")} prominent>{inspection.huNumber}</LabeledInfo>
                      <LabeledInfo label={t("quality.derogationCard.item")} prominent>{inspection.itemCodes}</LabeledInfo>
                      <LabeledInfo label={t("quality.derogationCard.quantity")} prominent>{inspection.quantity} {inspection.uom}</LabeledInfo>
                      <LabeledInfo label={t("quality.derogationCard.client")}>{inspection.clientName ?? inspection.clientCode ?? "—"}</LabeledInfo>
                      <LabeledInfo label={t("quality.derogationCard.warehouse")}>{inspection.warehouseCode}</LabeledInfo>
                    </Box>
                    <LabeledInfo label={t("quality.inspectionCard.reason")}>{reasonLabel(inspection.reasonCode)}</LabeledInfo>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
                      {inspection.asnNumber && <LabeledInfo label={t("quality.derogationCard.asn")}>{inspection.asnNumber}</LabeledInfo>}
                      {inspection.receiptNumber && <LabeledInfo label={t("quality.derogationCard.receipt")}>{inspection.receiptNumber}</LabeledInfo>}
                      {inspection.serialNumbers && <LabeledInfo label={t("quality.derogationCard.serial")}>{inspection.serialNumbers}</LabeledInfo>}
                    </Box>
                  </Stack>
                  )}
                  <ListRowActions actions={
                    inspection.status === "OPEN" && canExecute ? (
                      <Button variant="contained"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        startIcon={<AssignmentIndOutlined />}
                        data-testid={`quality-claim-${inspection.id}`}
                        disabled={claim.isPending}
                        onClick={() => claim.mutate(inspection)}>
                        {t("quality.actions.claim")}
                      </Button>
                    ) : inspection.status === "IN_PROGRESS"
                      && (canRelease || canRepack || canReturn || canScrap) ? (
                        <Button variant="contained"
                          sx={{ display: { xs: "none", sm: "inline-flex" } }}
                          data-testid={`quality-decide-open-${inspection.id}`}
                          onClick={() => setSelectedId(inspection.id)}>
                          {t(inspection.workflowState === "PENDING_EXECUTION"
                            ? "quality.actions.review" : "quality.actions.decide")}
                        </Button>
                      ) : undefined
                  } status={
                    <Chip label={operationalStatusLabel(
                      inspection.workflowState === "PARTNER_REVIEW"
                        ? "TRANSFERRED" : inspection.status
                    )} color={operationalStatusColor(
                      inspection.workflowState === "PARTNER_REVIEW"
                        ? "TRANSFERRED" : inspection.status
                    )} />
                  } />
                  <IconButton className="mobile-card-expand"
                    aria-label={t("quality.labels.details")}
                    onClick={() => setExpandedIds((current) =>
                      current.includes(inspection.id)
                        ? current.filter((id) => id !== inspection.id)
                        : [...current, inspection.id]
                    )}>
                    <ExpandMore sx={{ transition: "transform .2s",
                      transform: expandedIds.includes(inspection.id)
                        ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </IconButton>
                </Stack>
                <Collapse in={expandedIds.includes(inspection.id)} unmountOnExit>
                  <Box sx={{ mt: 2, ml: { sm: 7 }, p: 1.5,
                    bgcolor: "action.hover", borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip label={`${inspection.quantity} ${inspection.uom}`} />
                      <Chip label={inspection.processPointCode} />
                      <Chip label={inspection.warehouseCode} />
                    </Stack>
                    <Typography variant="overline" sx={{ mt: 1.5, display: "block" }}>
                      {t("quality.labels.itemTraceability")}
                    </Typography>
                    <Typography>{inspection.itemCodes}</Typography>
                    {inspection.serialNumbers && (
                      <Typography>{t("quality.labels.serialNumber", {
                        numbers: inspection.serialNumbers
                      })}</Typography>
                    )}
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      {reasonLabel(inspection.reasonCode)}
                      {inspection.reportedComment
                        ? ` — ${inspection.reportedComment}` : ""}
                    </Alert>
                    {inspection.assignedUserName && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t("quality.labels.assignedTo", {
                          user: inspection.assignedUserName
                        })}
                      </Typography>
                    )}
                    {inspection.disposition && (
                      <Alert severity="success" sx={{ mt: 1.5 }}>
                        <Typography>
                          {t("quality.labels.decision", {
                            decision: dispositionLabel(inspection.disposition)
                          })}
                          {inspection.dispositionComment
                            ? ` — ${inspection.dispositionComment}` : ""}
                        </Typography>
                        {isDerogation
                          && inspection.disposition === "ACCEPT_NEW_EXPIRY" && (
                          <Box sx={{ mt: 1 }}>
                            <Typography fontWeight={700}>
                              {t("quality.labels.acceptedTerms")}
                            </Typography>
                            <Stack direction={{ xs: "column", sm: "row" }}
                              spacing={3} useFlexGap flexWrap="wrap">
                              <Typography>{t("quality.labels.originalExpiry", {
                                date: inspection.originalExpirationDate ?? "—"
                              })}</Typography>
                              <Typography>{t("quality.labels.acceptedExtension", {
                                days: inspection.decidedExtensionDays
                                  ?? inspection.extensionDays ?? "—"
                              })}</Typography>
                              <Typography>{t("quality.labels.acceptedExpiry", {
                                date: inspection.decidedExpirationDate
                                  ?? inspection.requestedExpirationDate ?? "—"
                              })}</Typography>
                            </Stack>
                          </Box>
                        )}
                      </Alert>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
          </Card>
        ))}
      </Stack>

      <MobileActionBar primaryLabel={mobilePrimaryLabel}
        primaryDisabled={!mobilePrimaryAvailable}
        onPrimary={executeMobilePrimary} />

      <Dialog open={Boolean(selectedId)} onClose={() => setSelectedId(null)}
        fullWidth maxWidth="md">
        <DialogTitle>
          {t("quality.labels.inspection", {
            number: detail.data?.inspectionNumber ?? ""
          })}
          {detail.data ? ` · ${detail.data.huNumber}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {detail.isLoading && <CircularProgress />}
          {detail.data && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={operationalStatusLabel(
                  detail.data.workflowState === "PARTNER_REVIEW"
                    ? "TRANSFERRED" : detail.data.status
                )} color={operationalStatusColor(
                  detail.data.workflowState === "PARTNER_REVIEW"
                    ? "TRANSFERRED" : detail.data.status
                )} />
                <Chip label={`${detail.data.quantity} ${detail.data.uom}`} />
                <Chip label={detail.data.processPointCode} />
              </Stack>
              <SourceTrace sources={[
                detail.data.asnNumber, detail.data.receiptNumber,
                detail.data.huNumber, detail.data.inspectionNumber
              ]} step={t("quality.labels.qualityInspection")} client={detail.data.clientName
                ?? detail.data.clientCode} />
              <Box>
                <Typography variant="overline">
                  {t("quality.labels.itemTraceability")}
                </Typography>
                <Typography>{detail.data.itemCodes}</Typography>
                {detail.data.serialNumbers && (
                  <Typography>{t("quality.labels.serialNumber", {
                    numbers: detail.data.serialNumbers
                  })}</Typography>
                )}
              </Box>
              <Alert severity="warning">
                {reasonLabel(detail.data.reasonCode)}
                {" — "}{detail.data.reportedComment}
              </Alert>
              {isDerogation && (
                <Box>
                  <Typography variant="overline">
                    {t("quality.labels.expiryRequest")}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                    <Typography>{t("quality.labels.originalExpiry", {
                      date: detail.data.originalExpirationDate ?? "—"
                    })}</Typography>
                    <Typography>{t("quality.labels.requestedExtension", {
                      days: detail.data.extensionDays ?? "—"
                    })}</Typography>
                    <Typography>{t("quality.labels.requestedExpiry", {
                      date: detail.data.requestedExpirationDate ?? "—"
                    })}</Typography>
                  </Stack>
                  {detail.data.customerRequestStatus === "SUBMITTED" && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {t("quality.labels.customerRequestSubmitted", {
                        partner: detail.data.currentBusinessPartnerName ?? "—",
                        level: detail.data.currentValidationLevel ?? "—"
                      })}
                    </Alert>
                  )}
                  {detail.data.workflowState === "PENDING_EXECUTION" && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      <Typography>
                        {t("quality.labels.partnerDecisionPendingExecution", {
                          decision: detail.data.partnerDecision === "ACCEPTED"
                            ? t("quality.partnerActions.ACCEPTED")
                            : t("quality.partnerActions.REJECTED"),
                          comment: detail.data.partnerDecisionComment ?? "—"
                        })}
                      </Typography>
                      {detail.data.partnerDecision === "ACCEPTED" && (
                        <Box sx={{ mt: 1 }}>
                          <Typography fontWeight={700}>
                            {t("quality.labels.acceptedTerms")}
                          </Typography>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                            <Typography>{t("quality.labels.acceptedExtension", {
                              days: detail.data.decidedExtensionDays ?? "—"
                            })}</Typography>
                            <Typography>{t("quality.labels.acceptedExpiry", {
                              date: detail.data.decidedExpirationDate ?? "—"
                            })}</Typography>
                          </Stack>
                        </Box>
                      )}
                    </Alert>
                  )}
                </Box>
              )}
              {detail.data.assignedUserName && (
                <Typography color="text.secondary">
                  {t("quality.labels.assignedTo", {
                    user: detail.data.assignedUserName
                  })}
                </Typography>
              )}
              {detail.data.status === "IN_PROGRESS"
                && detail.data.workflowState !== "PARTNER_REVIEW"
                && (canRelease || canRepack || canReturn || canScrap) && (
                <>
                  <FormControl fullWidth required>
                    <InputLabel id="quality-disposition-label">
                      {t(isDerogation ? "quality.labels.derogationDecision"
                        : "quality.labels.qualityDecision")}
                    </InputLabel>
                    <Select labelId="quality-disposition-label"
                      label={t(isDerogation ? "quality.labels.derogationDecision"
                        : "quality.labels.qualityDecision")} value={disposition}
                      data-testid="quality-disposition"
                      disabled={detail.data.workflowState === "PENDING_EXECUTION"}
                      onChange={(event) => setDisposition(event.target.value as
                        NonNullable<QualityInspection["disposition"]>)}>
                      {isDerogation ? [
                        "ACCEPT_NEW_EXPIRY", "SUBMIT_TO_CUSTOMER", "SUBMIT_TO_SUPPLIER",
                        "RETURN_TO_SUPPLIER", "QUARANTINE"
                      ].map((value) => (
                        <MenuItem key={value} value={value}>
                          {t(`quality.dispositionActions.${value}`)}
                        </MenuItem>
                      )) : canRelease && (
                        <MenuItem value="RELEASED">
                          {t("quality.dispositionActions.RELEASED")}
                        </MenuItem>
                      )}
                      {!isDerogation && canRepack && (
                        <MenuItem value="REPACKAGING">
                          {t("quality.dispositionActions.REPACKAGING")}
                        </MenuItem>
                      )}
                      {!isDerogation && canReturn && (
                        <MenuItem value="RETURN_TO_SUPPLIER">
                          {t("quality.dispositionActions.RETURN_TO_SUPPLIER")}
                        </MenuItem>
                      )}
                      {!isDerogation && canScrap && (
                        <MenuItem value="SCRAPPED">
                          {t("quality.dispositionActions.SCRAPPED")}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  {isDerogation && disposition === "ACCEPT_NEW_EXPIRY" && (
                    <TextField type="number"
                      label={t("quality.labels.approvedExtensionDays")}
                      value={extensionDays}
                      disabled={detail.data.workflowState === "PENDING_EXECUTION"}
                      inputProps={{ min: 1, max: 3650 }}
                      onChange={(event) => setExtensionDays(
                        event.target.value === "" ? "" : Number(event.target.value)
                      )}
                      helperText={t("quality.help.extensionAdjustable")}
                      required />
                  )}
                  {!isDerogation && (disposition === "RELEASED"
                    || disposition === "REPACKAGING") && (
                  <FormControl fullWidth required>
                    <InputLabel id="quality-departure-point-label">
                      {disposition === "REPACKAGING"
                        ? t("quality.labels.repackagingStation")
                        : t("quality.labels.departureStation")}
                    </InputLabel>
                    <Select labelId="quality-departure-point-label"
                      label={disposition === "REPACKAGING"
                        ? t("quality.labels.repackagingStation")
                        : t("quality.labels.departureStation")}
                      data-testid="quality-departure-point"
                      value={departurePointId}
                      onChange={(event) =>
                        setDeparturePointId(event.target.value)}>
                      {eligibleDeparturePoints.map((point) => (
                          <MenuItem key={point.id} value={point.id}>
                            {point.code} · {point.displayName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  )}
                  {!isDerogation && (disposition === "RELEASED"
                    || disposition === "REPACKAGING")
                    && eligibleDeparturePoints.length === 0 && (
                    <Alert severity="warning">
                      {t("quality.errors.noCompatibleStation")}
                    </Alert>
                  )}
                  <TextField
                    label={t("quality.labels.decisionComment")}
                    data-testid="quality-comment"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    multiline minRows={3} required
                    helperText={t("quality.help.requiredThree")}
                  />
                </>
              )}
              {detail.data.disposition && (
                <Alert severity="success">
                  {t("quality.labels.decision", {
                    decision: dispositionLabel(detail.data.disposition)
                  })}.{" "}
                  {detail.data.dispositionComment}
                </Alert>
              )}
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedId(null)}>
            {t("common.actions.close")}
          </Button>
          {detail.data?.status === "OPEN" && canExecute && (
            <Button
              variant="contained"
              startIcon={<AssignmentIndOutlined />}
              data-testid="quality-claim"
              disabled={claim.isPending}
              onClick={() => claim.mutate(detail.data!)}
            >
              {t("quality.actions.claim")}
            </Button>
          )}
          {detail.data?.status === "IN_PROGRESS"
            && detail.data.workflowState !== "PARTNER_REVIEW"
            && (canRelease || canRepack || canReturn || canScrap) && (
            <Button
              variant="contained"
              data-testid="quality-decide"
              color={disposition === "RELEASED"
                || disposition === "ACCEPT_NEW_EXPIRY" ? "success"
                : disposition === "SCRAPPED"
                  || disposition === "QUARANTINE" ? "error" : "primary"}
              startIcon={<CheckCircleOutline />}
              disabled={(!isDerogation && (disposition === "RELEASED"
                || disposition === "REPACKAGING") && !departurePointId)
                || comment.trim().length < 3 || decide.isPending
                || (isDerogation && disposition === "ACCEPT_NEW_EXPIRY"
                  && (extensionDays === "" || extensionDays < 1
                    || extensionDays > 3650))}
              onClick={() => decide.mutate({
                inspection: detail.data!,
                value: comment.trim()
              })}
            >
              {t("quality.actions.confirmDecision")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
